import React, { useCallback, useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { PageHeader, SectionCard, Pill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useAuth, hasPermission } from "../context/AuthContext";
import { LABOUR_BAG_SCOPES, bagScopeLabel } from "../lib/bagSizes";
import { LABOUR_ACTIVITY_OPTIONS, labourActivityLabel } from "../lib/labourActivities";

const RATE_BASES = [
  { value: "PER_BAG", label: "Per bag" },
  { value: "PER_QTL", label: "Per qtl" },
  { value: "PER_TRUCK", label: "Per truck" },
  { value: "LUMP_SUM", label: "Lump sum" },
];

const DEFAULT_CONDITION_RULES = {
  height_tiers: [
    { label: "Wide stack", multiplier: "1.25" },
    { label: "Extra wide stack", multiplier: "1.5" },
  ],
  distance_tiers: [
    { label: "Long carry", multiplier: "1.25" },
    { label: "Very long carry", multiplier: "1.5" },
  ],
};

const emptyRateLine = () => ({
  activity_type: "UNLOAD_TO_STACK",
  rate_basis: "PER_BAG",
  bag_scope: "BAG_60KG",
  rate_amount: "",
});

export default function LabourContractsPage() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "labour.contract.manage");
  const [contracts, setContracts] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    party_id: "",
    valid_from: new Date().toISOString().slice(0, 10),
    valid_to: "",
    notes: "",
    condition_rules: DEFAULT_CONDITION_RULES,
    rate_lines: [emptyRateLine()],
  });
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [cRes, pRes] = await Promise.all([
        api.get("/labour-contracts", { params: { active_only: false } }),
        api.get("/parties/bootstrap", { params: { kind: "LABOUR_CONTRACTOR", active: "1" } }),
      ]);
      setContracts(cRes.data || []);
      setContractors(pRes.data?.results || []);
    } catch (err) {
      toast.error(extractError(err));
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const updateRateLine = (idx, patch) => {
    setForm((f) => ({
      ...f,
      rate_lines: f.rate_lines.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)),
    }));
  };

  const updateConditionTier = (kind, idx, patch) => {
    setForm((f) => ({
      ...f,
      condition_rules: {
        ...f.condition_rules,
        [`${kind}_tiers`]: f.condition_rules[`${kind}_tiers`].map((t, i) => (i === idx ? { ...t, ...patch } : t)),
      },
    }));
  };

  const addConditionTier = (kind) => {
    setForm((f) => ({
      ...f,
      condition_rules: {
        ...f.condition_rules,
        [`${kind}_tiers`]: [...f.condition_rules[`${kind}_tiers`], { label: "", multiplier: "" }],
      },
    }));
  };

  const removeConditionTier = (kind, idx) => {
    setForm((f) => ({
      ...f,
      condition_rules: {
        ...f.condition_rules,
        [`${kind}_tiers`]: f.condition_rules[`${kind}_tiers`].filter((_, i) => i !== idx),
      },
    }));
  };

  const buildConditionRulesPayload = () => {
    const cr = form.condition_rules;
    const mapTiers = (tiers) => tiers
      .map((t) => ({
        label: (t.label || "").trim() || undefined,
        excess: t.excess !== "" && t.excess != null ? Number(t.excess) : undefined,
        multiplier: Number(t.multiplier),
      }))
      .filter((t) => t.multiplier > 0 && (t.label || t.excess));
    return {
      height_tiers: mapTiers(cr.height_tiers || []),
      distance_tiers: mapTiers(cr.distance_tiers || []),
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.party_id) return toast.error("Select a labour contractor");
    if (!form.rate_lines.length) return toast.error("Add at least one rate line");
    if (form.rate_lines.some((ln) => ln.rate_basis === "PER_BAG" && !ln.bag_scope)) {
      return toast.error("Select bag size for each per-bag rate line");
    }
    const rate_lines = form.rate_lines.map((ln) => {
      const row = {
        activity_type: ln.activity_type,
        rate_basis: ln.rate_basis,
        rate_amount: Number(ln.rate_amount) || 0,
      };
      if (ln.rate_basis === "PER_BAG") {
        row.bag_scope = ln.bag_scope;
      }
      return row;
    });
    if (rate_lines.some((ln) => ln.rate_amount <= 0)) {
      return toast.error("Enter rate amount on each line");
    }
    try {
      setBusy(true);
      await api.post("/labour-contracts", {
        party_id: Number(form.party_id),
        valid_from: form.valid_from,
        valid_to: form.valid_to || undefined,
        notes: form.notes.trim() || undefined,
        condition_rules: buildConditionRulesPayload(),
        rate_lines,
      });
      toast.success("Labour contract created");
      setOpen(false);
      setForm({
        party_id: "",
        valid_from: new Date().toISOString().slice(0, 10),
        valid_to: "",
        notes: "",
        condition_rules: DEFAULT_CONDITION_RULES,
        rate_lines: [emptyRateLine()],
      });
      reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="labour-contracts-page">
      <PageHeader
        title="Labour Contracts"
        subtitle="Rates by movement activity (unload to stack, stack to houdi, etc.). Stack height = bags wide; carry distance = feet."
        actions={canManage ? (
          <Button
            type="button"
            className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
            onClick={() => setOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            New contract
          </Button>
        ) : null}
      />

      <SectionCard title="Active & closed contracts">
        <div className="space-y-3">
          {contracts.map((c) => (
            <div key={c.id} className="rounded-md border border-border p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-mono text-xs text-muted-foreground">{c.contract_number}</span>
                  <span className="font-medium ml-2">{c.party_name}</span>
                </div>
                <Pill tone={c.state === "ACTIVE" ? "sage" : "muted"}>{c.state}</Pill>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {c.valid_from}{c.valid_to ? ` → ${c.valid_to}` : ""}
              </p>
              {c.condition_rules && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  {(c.condition_rules.height_tiers?.length || 0)} stack width tier(s)
                  {" · "}
                  {(c.condition_rules.distance_tiers?.length || 0)} carry distance tier(s)
                </p>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                {(c.rate_lines || []).map((ln) => (
                  <Pill key={`${ln.id}-${ln.activity_type}-${ln.bag_scope || ""}`} tone="ivory" className="text-[10px]">
                    {labourActivityLabel(ln.activity_type)} · {ln.rate_basis.replace(/_/g, " ").toLowerCase()}
                    {ln.bag_scope ? ` · ${bagScopeLabel(ln.bag_scope)}` : ""} · ₹{ln.rate_amount}
                  </Pill>
                ))}
              </div>
            </div>
          ))}
          {!contracts.length && (
            <p className="text-sm text-muted-foreground py-6 text-center">No labour contracts yet.</p>
          )}
        </div>
      </SectionCard>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={submit}
            className="bg-card rounded-lg border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 space-y-4"
          >
            <h2 className="font-serif text-lg">New labour contract</h2>
            <div>
              <Label>Contractor</Label>
              <Select value={form.party_id} onValueChange={(v) => setForm((f) => ({ ...f, party_id: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select contractor" /></SelectTrigger>
                <SelectContent>
                  {contractors.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Valid from</Label>
                <Input type="date" value={form.valid_from} onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <Label>Valid to (optional)</Label>
                <Input type="date" value={form.valid_to} onChange={(e) => setForm((f) => ({ ...f, valid_to: e.target.value }))} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1" rows={2} />
            </div>
            <div className="space-y-3 rounded-md border border-border p-3 bg-muted/20">
              <div>
                <Label className="text-sm">Surcharge tiers (unload dropdowns)</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Supervisors pick &quot;Within limit&quot; or a surcharge tier per unload line — no numbers to type at unload.
                </p>
              </div>
              <ConditionTierEditor
                label="Stack width surcharge tiers"
                hint="Shown when unloading to stack"
                tiers={form.condition_rules.height_tiers}
                onUpdate={(idx, patch) => updateConditionTier("height", idx, patch)}
                onAdd={() => addConditionTier("height")}
                onRemove={(idx) => removeConditionTier("height", idx)}
              />
              <ConditionTierEditor
                label="Carry distance surcharge tiers"
                hint="Shown on every unload line"
                tiers={form.condition_rules.distance_tiers}
                onUpdate={(idx, patch) => updateConditionTier("distance", idx, patch)}
                onAdd={() => addConditionTier("distance")}
                onRemove={(idx) => removeConditionTier("distance", idx)}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Rate lines</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, rate_lines: [...f.rate_lines, emptyRateLine()] }))}>
                  <Plus className="w-3 h-3 mr-1" /> Add line
                </Button>
              </div>
              {form.rate_lines.map((ln, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end border rounded-md p-2">
                  <div className="col-span-4">
                    <Label className="text-[10px]">Activity</Label>
                    <Select value={ln.activity_type} onValueChange={(v) => updateRateLine(idx, { activity_type: v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LABOUR_ACTIVITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px]">Basis</Label>
                    <Select
                      value={ln.rate_basis}
                      onValueChange={(v) => updateRateLine(idx, {
                        rate_basis: v,
                        bag_scope: v === "PER_BAG" ? (ln.bag_scope || "BAG_60KG") : "",
                      })}
                    >
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RATE_BASES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {ln.rate_basis === "PER_BAG" ? (
                    <div className="col-span-3">
                      <Label className="text-[10px]">Bag size</Label>
                      <Select value={ln.bag_scope || ""} onValueChange={(v) => updateRateLine(idx, { bag_scope: v })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Bag size" /></SelectTrigger>
                        <SelectContent>
                          {LABOUR_BAG_SCOPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="col-span-3" />
                  )}
                  <div className="col-span-2">
                    <Label className="text-[10px]">₹ Rate</Label>
                    <Input className="h-8 text-xs" type="number" step="0.01" value={ln.rate_amount} onChange={(e) => updateRateLine(idx, { rate_amount: e.target.value })} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8" disabled={form.rate_lines.length <= 1} onClick={() => setForm((f) => ({ ...f, rate_lines: f.rate_lines.filter((_, i) => i !== idx) }))}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
                {busy ? "Saving…" : "Create contract"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function ConditionTierEditor({ label, hint, tiers, onUpdate, onAdd, onRemove }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div>
          <Label className="text-[10px]">{label}</Label>
          {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
        </div>
        <Button type="button" size="sm" variant="outline" className="h-7 text-[10px]" onClick={onAdd}>
          <Plus className="w-3 h-3 mr-1" /> Tier
        </Button>
      </div>
      {tiers.map((tier, idx) => (
        <div key={idx} className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-6">
            <Label className="text-[10px]">Label</Label>
            <Input
              className="h-8 text-xs"
              placeholder="e.g. Long carry"
              value={tier.label || ""}
              onChange={(e) => onUpdate(idx, { label: e.target.value })}
            />
          </div>
          <div className="col-span-4">
            <Label className="text-[10px]">Multiplier</Label>
            <Input
              className="h-8 text-xs"
              type="number"
              step="0.05"
              placeholder="1.25"
              value={tier.multiplier}
              onChange={(e) => onUpdate(idx, { multiplier: e.target.value })}
            />
          </div>
          <div className="col-span-2 flex justify-end">
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => onRemove(idx)}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      ))}
      {!tiers.length && (
        <p className="text-[10px] text-muted-foreground">No surcharge tiers — unload lines use within-limit rate only.</p>
      )}
    </div>
  );
}
